import { useState } from "react";
import { Plane, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function FlightQuote() {
  const [form, setForm] = useState({
    departure: "", destination: "", depart_date: "", return_date: "",
    passengers: 1, contact_name: "", contact_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/flight-quotes", { ...form, passengers: Number(form.passengers) });
      setDone(true);
      toast.success("Votre demande de devis a été envoyée !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="h-16 w-16 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-5">
          <Plane className="h-8 w-8 text-accent" />
        </div>
        <h1 className="font-head text-3xl font-extrabold text-primary mb-3">Demande reçue !</h1>
        <p className="text-muted-foreground mb-6">Notre équipe vous contactera très vite avec une proposition de vol adaptée.</p>
        <Button onClick={() => { setDone(false); setForm({ departure: "", destination: "", depart_date: "", return_date: "", passengers: 1, contact_name: "", contact_phone: "" }); }} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="new-quote-btn">
          Nouvelle demande
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="flex items-center gap-2 text-accent font-semibold mb-2"><Plane className="h-5 w-5" /> Devis vol</div>
      <h1 className="font-head text-4xl font-extrabold text-primary mb-2">Demander un devis de vol</h1>
      <p className="text-muted-foreground mb-8">Remplissez le formulaire, notre équipe vous enverra une proposition personnalisée.</p>

      <form onSubmit={submit} className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-primary">Ville de départ</label>
            <Input required value={form.departure} onChange={set("departure")} placeholder="Ex. Douala" className="rounded-xl" data-testid="fq-departure" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Destination</label>
            <Input required value={form.destination} onChange={set("destination")} placeholder="Ex. Paris" className="rounded-xl" data-testid="fq-destination" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Date de départ</label>
            <Input required type="date" value={form.depart_date} onChange={set("depart_date")} className="rounded-xl" data-testid="fq-depart-date" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Date de retour (optionnel)</label>
            <Input type="date" value={form.return_date} onChange={set("return_date")} className="rounded-xl" data-testid="fq-return-date" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Nombre de passagers</label>
            <Input required type="number" min={1} value={form.passengers} onChange={set("passengers")} className="rounded-xl" data-testid="fq-passengers" />
          </div>
          <div>
            <label className="text-sm font-semibold text-primary">Votre nom</label>
            <Input required value={form.contact_name} onChange={set("contact_name")} placeholder="Nom complet" className="rounded-xl" data-testid="fq-name" />
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold text-primary">Téléphone / contact</label>
          <Input required value={form.contact_phone} onChange={set("contact_phone")} placeholder="Ex. 6XX XX XX XX" className="rounded-xl" data-testid="fq-phone" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="fq-submit">
          {submitting ? "Envoi..." : <><Send className="mr-2 h-4 w-4" /> Envoyer ma demande</>}
        </Button>
      </form>
    </div>
  );
}
